<template>
  <div class="coupon-usage-log-container" @click="closeOverlays">
    <h1 class="page-title">Coupon Usage Log</h1>

    <div class="filter-section">
      <div class="filter-row">
        <div
          class="search-wrapper field-search-item"
          :class="{ 'is-open': openDd === 'searchField' }"
        >
          <div
            class="search-select-wrapper"
            :class="{ 'is-open': openDd === 'searchField' }"
            @click.stop="toggleSearchFieldDropdown"
          >
            <span class="search-select-label">{{ currentSearchFieldLabel }}</span>
            <img :src="downarrowIcon" alt="" class="dropdown-arrow" />
          </div>
          <input
            v-model="filters.searchQuery"
            type="text"
            class="search-input"
            placeholder="Search.."
            maxlength="50"
            @keyup.enter="handleSearch"
            @focus="openDd = null"
          />
          <div v-if="openDd === 'searchField'" class="dropdown-menu" @click.stop>
            <div
              v-for="option in searchFieldOptions"
              :key="option.value"
              class="dropdown-item"
              :class="{ selected: filters.searchField === option.value }"
              @click="selectSearchField(option.value)"
            >
              {{ option.label }}
            </div>
          </div>
        </div>

        <div class="filter-box filter-box--date" :class="{ 'is-open': openCal }">
          <span class="filter-label-inline">Usage Date:</span>
          <div class="date-calendar-anchor">
            <div class="date-input-wrap" @click.stop="toggleCal">
              <input
                type="text"
                readonly
                class="filter-date-input"
                :value="dateDisplay"
                placeholder="Select a date"
                :class="{ 'is-placeholder': !filters.usageDate }"
              />
              <span class="date-cal-split" aria-hidden="true" />
              <img :src="calendarIcon" alt="" class="calendar-icon" />
            </div>
            <div v-if="openCal" class="calendar-pop" @click.stop>
              <CalendarMini
                v-model:month="calMonth"
                v-model:year="calYear"
                :selected-ymd="filters.usageDate"
                @pick="onPickDate"
              />
            </div>
          </div>
        </div>

        <div class="filter-actions-inline">
          <button class="btn-search" type="button" @click="handleSearch">Search</button>
          <button class="btn-clear" type="button" @click="handleClear">Clear</button>
        </div>
      </div>
    </div>

    <div class="records-bar">
      <span class="count-info" :class="{ 'count-info--pending': pageView === 'loading' }">
        {{ pageView === 'loading' ? 0 : totalCount }} Records
      </span>
    </div>

    <div class="list-body" :aria-busy="pageView === 'loading'">
      <LoadingIndicator v-if="pageView === 'loading'" />
      <div v-else-if="totalCount === 0" class="orders-empty">
        <img :src="imgListBlank" class="orders-empty-img" alt="" />
        <p class="orders-empty-msg">No records found</p>
      </div>
      <div v-else class="table-section">
        <table class="usage-table">
          <thead>
            <tr>
              <th>Coupon Code</th>
              <th>Discount Amount</th>
              <th>Final Amount Paid</th>
              <th>Applied Order ID</th>
              <th>User Mobile</th>
              <th>Usage Time</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in displayRows" :key="row.id">
              <td>
                <button type="button" class="cell-link" @click.stop="handleCouponCodeClick(row)">
                  {{ row.couponCode }}
                </button>
              </td>
              <td>{{ row.discountAmount }}</td>
              <td>{{ row.finalAmountPaid }}</td>
              <td>
                <button type="button" class="cell-link" @click.stop="handleOrderClick(row)">
                  {{ row.appliedOrderId }}
                </button>
              </td>
              <td>
                <button type="button" class="cell-link" @click.stop="handleMobileClick(row)">
                  {{ row.userMobile }}
                </button>
              </td>
              <td>{{ row.usageTime }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="pagination-wrapper">
      <PageComponent
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-change="handlePageChange"
      />
    </div>

    <Teleport to="body">
      <CouponDetail
        v-if="showCouponDetail && currentCouponId"
        :coupon-id="currentCouponId"
        @close="handleCouponDetailClose"
        @set-visibility="handleDetailSetVisibility"
        @request-status="handleDetailRequestStatus"
        @updated="handleDetailUpdated"
      />

      <CenterActivate
        v-if="showStatusModal && statusCouponId"
        :coupon-id="statusCouponId"
        :record-id="statusRecordId"
        :coupon-name="statusCouponName"
        :action="statusAction"
        @close="handleStatusClose"
        @saved="handleStatusSaved"
        @failed="handleStatusFailed"
      />

      <SetVisibility
        v-if="showVisibilityModal && visibilityCouponId"
        :coupon-id="visibilityCouponId"
        :record-id="visibilityRecordId"
        :initial-visibility="visibilityInitial"
        @close="handleVisibilityClose"
        @saved="handleVisibilitySaved"
        @failed="handleVisibilityFailed"
      />

      <UserDetail
        v-if="showUserDetail && currentUserId"
        :user-id="currentUserId"
        @close="handleUserDetailClose"
      />

      <div v-if="orderDetailOverlayOrder" class="cod-overlay-root">
        <div class="cod-overlay-shell">
          <CareOrderDetailPanel
            :order="orderDetailOverlayOrder"
            @close="closeOrderDetailOverlay"
          />
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <ToastComponent :visible="toast.visible" :type="toast.type" :message="toast.message" />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, provide, reactive, ref } from 'vue'
import PageComponent from '@/components/PageComponent.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import ToastComponent from '@/components/ToastComponent.vue'
import CalendarMini from '@/views/Home/components/CalendarMini.vue'
import CareOrderDetailPanel from '@/views/Home/components/CareOrderDetailPanel.vue'
import CouponDetail, {
  type CouponDetailRequestStatusPayload,
  type CouponDetailSetVisibilityPayload,
  type CouponDetailUpdatePayload,
} from '@/views/CouponsPage/CouponDetail.vue'
import CenterActivate from '@/views/CouponsPage/CenterActivate.vue'
import SetVisibility from '@/views/CouponsPage/SetVisibility.vue'
import UserDetail from '@/views/userspage/components/UserDetail.vue'
import { TOAST_DURATION } from '@/constants/toast'
import {
  buildCouponUsageLogParams,
  mapCouponUsageLogItem,
  postCouponUsageList,
  resolveCouponDetailId,
  resolveUserDetailId,
  type CouponUsageLogRow,
} from '@/api/CouponUsageLog'
import {
  buildUsageSearchTypes,
  DEFAULT_USAGE_SEARCH_TYPES,
  postCouponUsageMeta,
  type CouponUsageMetaOption,
} from '@/api/CouponUsageMeta'
import {
  careOrderListItemToCardDto,
  extractCareOrderListArray,
  parseCareOrderListResponse,
  postCareOrderDetail,
  readCareOrderNumericId,
  type OrderCardDTO,
} from '@/api/orders'
import { fetchCareOrderListCached } from '@/utils/careOrderListCache'
import { mapCareOrderListToCards } from '@/utils/technicianNoFromList'
import calendarIcon from '@/assets/编组 2@2x.png'
import downarrowIcon from '@/assets/downarrow.png'
import imgListBlank from '@/assets/list_blank@2x.png'

const toast = reactive({
  visible: false,
  type: 'success' as 'success' | 'warning' | 'error',
  message: '',
})

function showToast(type: 'success' | 'warning' | 'error', message: string): void {
  toast.type = type
  toast.message = message
  toast.visible = true
  window.setTimeout(() => {
    toast.visible = false
  }, TOAST_DURATION.brief)
}

provide<(type: 'success' | 'warning' | 'error', message: string) => void>('showToast', showToast)

const PAGE_SIZE = 10
const LOADING_MIN_MS = 1500

type PageView = 'loading' | 'content'

const now = new Date()
const calMonth = ref(now.getMonth())
const calYear = ref(now.getFullYear())
const openCal = ref(false)
const openDd = ref<'searchField' | null>(null)
const currentPage = ref(1)
const totalCount = ref(0)
const totalPages = ref(1)
const pageView = ref<PageView>('loading')
let fetchGeneration = 0
const displayRows = ref<CouponUsageLogRow[]>([])
const searchFieldOptions = ref<CouponUsageMetaOption[]>([...DEFAULT_USAGE_SEARCH_TYPES])
const showCouponDetail = ref(false)
const currentCouponId = ref('')
const showStatusModal = ref(false)
const statusCouponId = ref('')
const statusRecordId = ref(0)
const statusCouponName = ref('')
const statusAction = ref<'deactivate' | 'reactivate'>('deactivate')
const showVisibilityModal = ref(false)
const visibilityCouponId = ref('')
const visibilityRecordId = ref(0)
const visibilityInitial = ref<'visible' | 'hidden'>('visible')
const showUserDetail = ref(false)
const currentUserId = ref('')
const orderDetailOverlayOrder = ref<OrderCardDTO | null>(null)
const detailResolving = ref(false)

const filters = reactive({
  searchField: 'coupon_code',
  searchQuery: '',
  usageDate: '',
})

const appliedFilters = reactive({
  searchField: 'coupon_code',
  searchQuery: '',
  usageDate: '',
})

const currentSearchFieldLabel = computed(() => {
  const hit = searchFieldOptions.value.find((option) => option.value === filters.searchField)
  return hit?.label ?? 'Coupon Code'
})

function ensureValidSearchField(): void {
  const values = searchFieldOptions.value.map((option) => option.value)
  if (!values.includes(filters.searchField)) {
    const fallback = searchFieldOptions.value[0]?.value ?? 'coupon_code'
    filters.searchField = fallback
    appliedFilters.searchField = fallback
  }
}

async function fetchUsageMeta(): Promise<void> {
  try {
    const response = await postCouponUsageMeta()
    if (response.code === 200) {
      searchFieldOptions.value = buildUsageSearchTypes(response.data)
      ensureValidSearchField()
    }
  } catch (error) {
    console.error('Fetch coupon usage meta error:', error)
  }
}

const dateDisplay = computed(() => {
  if (!filters.usageDate) return ''
  const [y, m, d] = filters.usageDate.split('-').map(Number)
  if (!y || !m || !d) return filters.usageDate
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
})

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function fetchUsageList(): Promise<void> {
  const generation = ++fetchGeneration
  pageView.value = 'loading'
  try {
    const [response] = await Promise.all([
      postCouponUsageList(
        buildCouponUsageLogParams({
          page: currentPage.value,
          pageSize: PAGE_SIZE,
          searchField: appliedFilters.searchField,
          searchQuery: appliedFilters.searchQuery,
          usageDate: appliedFilters.usageDate,
        }),
      ),
      delay(LOADING_MIN_MS),
    ])

    if (generation !== fetchGeneration) return

    if (response.code === 200 && response.data) {
      displayRows.value = (response.data.list ?? []).map(mapCouponUsageLogItem)
      totalCount.value = response.data.pagination?.total ?? displayRows.value.length
      totalPages.value = Math.max(1, response.data.pagination?.total_pages ?? 1)
      return
    }

    displayRows.value = []
    totalCount.value = 0
    totalPages.value = 1
  } catch (error) {
    if (generation !== fetchGeneration) return
    console.error('Fetch coupon usage list error:', error)
    displayRows.value = []
    totalCount.value = 0
    totalPages.value = 1
  } finally {
    if (generation === fetchGeneration) {
      pageView.value = 'content'
    }
  }
}

function closeOverlays(): void {
  openCal.value = false
  openDd.value = null
}

function toggleSearchFieldDropdown(): void {
  openCal.value = false
  openDd.value = openDd.value === 'searchField' ? null : 'searchField'
}

function selectSearchField(value: string): void {
  filters.searchField = value
  openDd.value = null
}

function toggleCal(): void {
  openDd.value = null
  openCal.value = !openCal.value
}

function onPickDate(ymd: string): void {
  filters.usageDate = ymd
  openCal.value = false
}

function handleSearch(): void {
  appliedFilters.searchField = filters.searchField
  appliedFilters.searchQuery = filters.searchQuery
  appliedFilters.usageDate = filters.usageDate
  currentPage.value = 1
  openDd.value = null
  void fetchUsageList()
}

function handleClear(): void {
  filters.searchField = 'coupon_code'
  filters.searchQuery = ''
  filters.usageDate = ''
  appliedFilters.searchField = 'coupon_code'
  appliedFilters.searchQuery = ''
  appliedFilters.usageDate = ''
  currentPage.value = 1
  openCal.value = false
  openDd.value = null
  void fetchUsageList()
}

function handlePageChange(page: number): void {
  currentPage.value = page
  void fetchUsageList()
}

function orderDisplayIdMatches(order: OrderCardDTO, orderId: string): boolean {
  const key = orderId.trim().toLowerCase()
  if (!key) return false
  const id = String(order.id ?? '').trim().toLowerCase()
  if (id === key) return true
  const pk = order.orderPrimaryId
  if (pk != null && Number.isFinite(pk)) {
    if (String(pk).toLowerCase() === key) return true
    if (`hm-${pk}`.toLowerCase() === key) return true
  }
  return false
}

function parseOrderListFromRaw(raw: unknown): OrderCardDTO[] {
  if (raw != null && typeof raw === 'object') {
    const response = raw as Record<string, unknown>
    if (response.code === 200 && response.data != null && typeof response.data === 'object') {
      const listRaw = extractCareOrderListArray(response.data as Record<string, unknown>)
      if (listRaw) return mapCareOrderListToCards(listRaw, null)
    }
  }
  const parsed = parseCareOrderListResponse(raw)
  return parsed?.list ?? []
}

function pickPrimaryIdFromRawList(raw: unknown, orderId: string): number | undefined {
  if (raw == null || typeof raw !== 'object') return undefined
  const response = raw as Record<string, unknown>
  const data = response.data
  if (data == null || typeof data !== 'object') return undefined
  const listRaw = extractCareOrderListArray(data as Record<string, unknown>)
  if (!listRaw?.length) return undefined

  const key = orderId.trim().toLowerCase()
  for (const item of listRaw) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const displayId = String(row.order_id ?? '').trim().toLowerCase()
    if (key && displayId && displayId !== key && !displayId.includes(key) && !key.includes(displayId)) {
      continue
    }
    const pk =
      readCareOrderNumericId(row.id) ??
      readCareOrderNumericId(row.order_primary_id) ??
      readCareOrderNumericId(row.care_order_id)
    if (pk != null) return pk
  }

  const first = listRaw[0]
  if (first && typeof first === 'object') {
    const row = first as Record<string, unknown>
    return (
      readCareOrderNumericId(row.id) ??
      readCareOrderNumericId(row.order_primary_id) ??
      readCareOrderNumericId(row.care_order_id)
    )
  }
  return undefined
}

async function fetchOrderCardForRow(row: CouponUsageLogRow): Promise<OrderCardDTO | null> {
  if (row.orderPrimaryId) {
    try {
      const detail = await postCareOrderDetail(row.orderPrimaryId)
      return careOrderListItemToCardDto(detail)
    } catch (error) {
      console.warn('Fetch order detail by primary id failed:', error)
    }
  }

  const orderId = row.appliedOrderId.trim()
  if (!orderId) return null

  try {
    const raw = await fetchCareOrderListCached({
      page: 1,
      pageSize: 20,
      searchField: 'order_id',
      searchQuery: orderId,
    })
    const list = parseOrderListFromRaw(raw)
    let card = list.find((order) => orderDisplayIdMatches(order, orderId)) ?? list[0] ?? null

    if (card && (card.orderPrimaryId == null || card.orderPrimaryId <= 0)) {
      const pk = pickPrimaryIdFromRawList(raw, orderId)
      if (pk != null) {
        card = { ...card, orderPrimaryId: pk }
      }
    }

    if (!card?.orderPrimaryId) return null

    try {
      const detail = await postCareOrderDetail(card.orderPrimaryId)
      return careOrderListItemToCardDto(detail)
    } catch (error) {
      console.warn('Fetch order detail after list resolve failed:', error)
      return card
    }
  } catch (error) {
    console.error('Fetch order card error:', error)
    return null
  }
}

async function handleCouponCodeClick(row: CouponUsageLogRow): Promise<void> {
  if (detailResolving.value) return
  detailResolving.value = true
  try {
    const couponId = await resolveCouponDetailId(row)
    if (!couponId) {
      showToast('warning', 'Coupon not found.')
      return
    }
    currentCouponId.value = couponId
    showCouponDetail.value = true
  } finally {
    detailResolving.value = false
  }
}

function handleCouponDetailClose(): void {
  showCouponDetail.value = false
  currentCouponId.value = ''
}

function handleDetailRequestStatus(payload: CouponDetailRequestStatusPayload): void {
  if (!currentCouponId.value || !payload.recordId) {
    showToast('error', 'Coupon record ID is missing')
    return
  }
  statusCouponId.value = currentCouponId.value
  statusRecordId.value = payload.recordId
  statusCouponName.value = payload.couponName
  statusAction.value = payload.action
  showStatusModal.value = true
}

function handleStatusClose(): void {
  showStatusModal.value = false
  statusCouponId.value = ''
  statusRecordId.value = 0
  statusCouponName.value = ''
}

function handleStatusSaved(): void {
  const action = statusAction.value
  const message =
    action === 'deactivate' ? 'Successfully deactivated' : 'Successfully reactivated'
  handleStatusClose()
  handleCouponDetailClose()
  void nextTick(() => {
    showToast('success', message)
  })
}

function handleStatusFailed(): void {
  handleStatusClose()
}

function handleDetailUpdated(payload: CouponDetailUpdatePayload): void {
  const message =
    payload.availability === 'inactive' ? 'Successfully deactivated' : 'Successfully reactivated'
  handleCouponDetailClose()
  void nextTick(() => {
    showToast('success', message)
  })
}

function handleDetailSetVisibility(payload: CouponDetailSetVisibilityPayload): void {
  if (!currentCouponId.value || !payload.recordId) {
    showToast('error', 'Coupon record ID is missing')
    return
  }
  visibilityCouponId.value = currentCouponId.value
  visibilityRecordId.value = payload.recordId
  visibilityInitial.value = payload.visibility
  showVisibilityModal.value = true
}

function handleVisibilityClose(): void {
  showVisibilityModal.value = false
  visibilityCouponId.value = ''
  visibilityRecordId.value = 0
}

function handleVisibilitySaved(): void {
  showVisibilityModal.value = false
  visibilityCouponId.value = ''
  visibilityRecordId.value = 0
  handleCouponDetailClose()
  void nextTick(() => {
    showToast('success', 'Successfully set')
  })
}

function handleVisibilityFailed(): void {
  // 错误提示由 SetVisibility 弹窗内 toast 展示
}

async function handleOrderClick(row: CouponUsageLogRow): Promise<void> {
  if (detailResolving.value) return
  const orderId = row.appliedOrderId.trim()
  if (!orderId && !row.orderPrimaryId) {
    showToast('warning', 'Order not found.')
    return
  }

  detailResolving.value = true
  try {
    const order = await fetchOrderCardForRow(row)
    if (!order) {
      showToast('warning', 'Order not found.')
      return
    }
    orderDetailOverlayOrder.value = order
  } finally {
    detailResolving.value = false
  }
}

function closeOrderDetailOverlay(): void {
  orderDetailOverlayOrder.value = null
}

async function handleMobileClick(row: CouponUsageLogRow): Promise<void> {
  if (detailResolving.value) return
  const mobile = row.userMobile.trim()
  if (!mobile && !row.userId.trim()) {
    showToast('warning', 'User not found.')
    return
  }

  detailResolving.value = true
  try {
    const userId = await resolveUserDetailId(row)
    if (!userId) {
      showToast('warning', 'User not found.')
      return
    }

    // 不再预拉 detail；交由 UserDetail 只请求一次详情
    currentUserId.value = userId
    showUserDetail.value = true
  } finally {
    detailResolving.value = false
  }
}

function handleUserDetailClose(): void {
  showUserDetail.value = false
  currentUserId.value = ''
}

function onDocClick(event: MouseEvent): void {
  const target = event.target as HTMLElement
  if (
    target.closest('.search-select-wrapper') ||
    target.closest('.dropdown-menu') ||
    target.closest('.date-input-wrap') ||
    target.closest('.calendar-pop')
  ) {
    return
  }
  closeOverlays()
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
  void initPage()
})

async function initPage(): Promise<void> {
  await fetchUsageMeta()
  await fetchUsageList()
}

onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
})
</script>

<style scoped>
.coupon-usage-log-container {
  width: 100%;
  padding: 0;
  height: auto;
  overflow: visible;
  box-sizing: border-box;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0 0 16px;
  line-height: 26px;
}

.filter-section {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
  overflow: visible;
}

.filter-row {
  display: grid;
  grid-template-columns: 273px 273px auto;
  gap: 16px;
  align-items: center;
  width: 100%;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #ffffff;
  width: 273px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  flex-shrink: 0;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
}

.search-wrapper:focus-within,
.search-wrapper.is-open {
  border-color: #666666;
}

.search-select-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 12px;
  cursor: pointer;
  user-select: none;
  position: relative;
  flex-shrink: 0;
}

.search-select-wrapper::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 16px;
  background-color: #e0e0e0;
}

.search-select-label {
  font-size: 14px;
  color: #000000;
  font-weight: 600;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  white-space: nowrap;
}

.dropdown-arrow {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  object-fit: contain;
  pointer-events: none;
  transition: transform 0.2s ease;
  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg)
    brightness(100%) contrast(100%);
}

.search-select-wrapper.is-open .dropdown-arrow {
  transform: rotate(180deg);
}

.search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 400;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  background: transparent;
  padding: 0 0 0 12px;
}

.search-input::placeholder {
  color: #cccccc;
  font-weight: 400;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: -1px;
  width: 273px;
  box-sizing: border-box;
  padding: 4px 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dropdown-item {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.dropdown-item.selected {
  font-weight: 600;
}

.filter-box {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  width: 273px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  flex-shrink: 0;
  padding: 0 12px;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
}

.filter-box:focus-within,
.filter-box.is-open {
  border-color: #666666;
}

.filter-box--date {
  gap: 8px;
}

.date-calendar-anchor {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}

.date-input-wrap {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.filter-label-inline {
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  color: #000000;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  white-space: nowrap;
}

.filter-date-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  padding: 0;
  cursor: pointer;
}

.filter-date-input::placeholder {
  color: #cccccc;
  font-weight: 400;
}

.filter-date-input.is-placeholder {
  color: #cccccc;
  font-weight: 400;
  -webkit-text-fill-color: #cccccc;
}

.date-cal-split {
  flex-shrink: 0;
  width: 1px;
  height: 16px;
  margin: 0 8px;
  background: #e0e0e0;
}

.calendar-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  flex-shrink: 0;
  cursor: pointer;
}

.calendar-pop {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 35;
}

.filter-actions-inline {
  display: flex;
  align-items: center;
  gap: 16px;
  justify-self: end;
}

.btn-search {
  width: 80px;
  height: 32px;
  padding: 0;
  background-color: #000000;
  color: #ffffff;
  border: none;
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro',
    system-ui,
    sans-serif;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-search:hover {
  background-color: #484848;
}

.btn-clear {
  width: 80px;
  height: 32px;
  padding: 0;
  background-color: #b0b0b0;
  color: #ffffff;
  border: none;
  border-radius: 16px;
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro',
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-clear:hover {
  background-color: #cccccc;
}

.records-bar {
  margin-bottom: 16px;
}

.count-info {
  font-size: 16px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  min-height: 24px;
  line-height: 24px;
}

.count-info--pending {
  visibility: hidden;
}

.list-body {
  position: relative;
}

.orders-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
  box-sizing: border-box;
}

.orders-empty-img {
  width: 200px;
  height: 200px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.orders-empty-msg {
  margin: 12px 0 0;
  font-size: 24px;
  font-weight: 500;
  color: #b0b0b0;
}

.table-section {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  overflow: hidden;
  box-sizing: border-box;
}

.usage-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.usage-table tr {
  height: 56px;
}

.usage-table th,
.usage-table td {
  box-sizing: border-box;
  height: 56px;
  min-height: 56px;
  max-height: 56px;
  padding: 0 12px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  line-height: 16px;
  color: #000000;
  white-space: nowrap;
  border: none;
  vertical-align: middle;
  background: #ffffff;
  box-shadow: inset 0 -1px 0 0 #e0e0e0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.usage-table th {
  font-weight: 700;
  text-align: center;
}

.usage-table th:first-child {
  border-radius: 16px 0 0 0;
}

.usage-table th:last-child {
  border-radius: 0 16px 0 0;
}

.usage-table td {
  font-weight: 400;
  text-align: center;
}

.usage-table tbody tr:last-child td {
  box-shadow: none;
}

.usage-table tbody tr:last-child td:first-child {
  border-radius: 0 0 0 16px;
}

.usage-table tbody tr:last-child td:last-child {
  border-radius: 0 0 16px 0;
}

.cell-link {
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  color: #000000;
  text-decoration: underline;
  text-decoration-color: #000000;
  text-underline-offset: 2px;
  cursor: pointer;
}

.cell-link:hover {
  color: #000000;
}

.pagination-wrapper {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.cod-overlay-root {
  position: fixed;
  inset: 0;
  z-index: 12400;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;
  padding: 24px 24px 40px;
}

.cod-overlay-shell {
  width: min(540px, 100%);
  height: calc(100vh - 64px);
  max-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.cod-overlay-shell :deep(.cod-panel) {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
  height: 100%;
}

@media (max-width: 1200px) {
  .filter-row {
    grid-template-columns: 273px 273px;
  }

  .filter-actions-inline {
    grid-column: 1 / -1;
    justify-self: end;
  }
}

@media (max-width: 720px) {
  .filter-row {
    grid-template-columns: 1fr;
  }

  .search-wrapper,
  .filter-box {
    width: 100%;
    max-width: 273px;
  }

  .dropdown-menu {
    width: 100%;
    max-width: 273px;
  }

  .filter-actions-inline {
    grid-column: auto;
    justify-self: stretch;
    justify-content: flex-end;
  }
}
</style>
